import React, { useCallback, useEffect } from 'react';
import { getDebugRing } from '@lib/devUtils';
import { clx } from '@lib/utils';
import { Camera } from '@/components/icons/Camera';
import { type ICameraPhoto, useNativeCamera } from '@/features/nativeBridge';
import { PhotoComponent } from '../PhotoComponent';
import { createPhotoFileFromNativeBase64 } from './utils';

interface IAddPhotoProps {
  className?: string;
  photoFile?: File;
  setPhotoFile: (photoFile?: File) => void;
}

export const AddPhoto = ({ className, photoFile, setPhotoFile }: IAddPhotoProps) => {
  const {
    photo: nativePhoto,
    isCapturing,
    error,
    requestCamera,
    capturePhoto,
    // clearPhoto,
  } = useNativeCamera();

  // For file input.
  const handlePhotoChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      setPhotoFile(file);
    },
    [setPhotoFile]
  );

  // For native camera.
  const handleNativePhotoCapture = useCallback(
    (nativePhoto: ICameraPhoto) => {
      try {
        const photo = createPhotoFileFromNativeBase64(nativePhoto);
        setPhotoFile(photo);
      } catch (error) {
        console.error('Error creating photo from native photo.', error);
      }
    },
    [setPhotoFile]
  );

  // Handle native photo capture.
  useEffect(() => {
    if (nativePhoto) {
      handleNativePhotoCapture(nativePhoto);
    }
  }, [handleNativePhotoCapture, nativePhoto]);

  return (
    <div className={clx('w-full flex-1', className)}>
      {/* Instructions */}
      <div
        className={clx(
          'mx-auto mb-3 flex w-10/12 flex-col items-center justify-center text-center',
          getDebugRing('green')
        )}
      >
        <h1 className="mb-7 text-lg text-black">Add a photo:</h1>

        {photoFile ? (
          <div className="mb-4">
            <PhotoComponent photoUrl={URL.createObjectURL(photoFile)} />
            <button
              onClick={() => {
                setPhotoFile(undefined);
              }}
              className="mt-2 text-sm text-red-600 underline"
            >
              Remove Photo
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center space-y-4">
            <Camera />

            {/* Native Camera Button */}
            <div className="flex space-x-2">
              <button
                onClick={requestCamera}
                className="rounded-lg bg-blue-500 px-4 py-2 text-white transition-colors hover:bg-blue-600"
              >
                Open Native Camera
              </button>
              <button
                onClick={capturePhoto}
                disabled={isCapturing}
                className="rounded-lg bg-green-500 px-4 py-2 text-white transition-colors hover:bg-green-600 disabled:opacity-50"
              >
                {isCapturing ? 'Capturing...' : 'Capture Photo'}
              </button>
            </div>

            {/* File Input Fallback
            this probably opens the camera and the file picker on iOS,
            but only opens the file picker on Android.
             */}
            <div className="text-sm text-gray-600">
              <span>Or choose from files:</span>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoChange}
                className="ml-2"
              />
            </div>

            {/* Error Display */}
            {error && <div className="text-sm text-red-600">{error}</div>}

            {/* Capturing Indicator */}
            {isCapturing && <div className="text-sm text-blue-600">Capturing photo...</div>}
          </div>
        )}
      </div>
    </div>
  );
};
